'use client';

import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Group, Text, Tooltip } from '@mantine/core';

import TextInput from '@ui-kit/molecules/TextInput';
import ResourceIcon from '@features/Resource/ResourceIcon';

import { ResourceInput } from './types';

interface SimpleViewProps {
  resources: ResourceInput[];
  suggestedNames: Record<string, string>;
  onNameChange: (resourceId: string, value: string) => void;
}

export default function SimpleView({
  resources,
  suggestedNames,
  onNameChange,
}: SimpleViewProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: resources.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      style={{
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const resource = resources[virtualRow.index];
          const suggestedName = suggestedNames[resource.id];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: '1rem',
              }}
            >
              <Group gap="md" wrap="nowrap" align="center">
                <Tooltip label={resource.name} openDelay={400} position="top">
                  <Group
                    gap="0.4rem"
                    wrap="nowrap"
                    align="center"
                    style={{ flex: '0 0 35%', minWidth: 0, overflow: 'hidden' }}
                  >
                    <ResourceIcon resource={resource} size={16} withColor />
                    <Text size="sm" c="dimmed" truncate>
                      {resource.name}
                    </Text>
                  </Group>
                </Tooltip>
                <TextInput
                  value={suggestedName}
                  onChange={(event) =>
                    onNameChange(resource.id, event.currentTarget.value)
                  }
                  autoFocus={virtualRow.index === 0}
                  style={{ flex: 1 }}
                />
              </Group>
            </div>
          );
        })}
      </div>
    </div>
  );
}
